/**
 * F16.C: React context that ties a `/c/${slug}` route subtree to its
 * portal's MSAL instance. Acquires the instance on mount, releases on
 * unmount (the registry handles the 5-min idle delay), and exposes
 * `signIn` / `signOut` / `account` to descendants via `usePortalMsal`.
 *
 * The provider intentionally accepts the `lockedTenantId` as a prop rather
 * than re-reading it from `useTenantBranding` so it can be mounted by
 * routes that have already resolved the branding row.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type { AccountInfo } from '@azure/msal-browser';
import {
  acquireInstance,
  releaseInstance,
  startPortalRedirectSignIn,
  disconnectPortalAccount,
  getActiveAccountForTenant,
} from './msalInstanceRegistry';

export interface PortalMsalContextValue {
  lockedTenantId: string;
  ready: boolean;
  account: AccountInfo | null;
  /**
   * F16.L: signIn navigates the SPA away (loginRedirect). Callers
   * SHOULD NOT depend on this resolving with the AccountInfo — the
   * page is unloading by the time it'd return. Resolved value is
   * always `null` in the redirect flow.
   *
   * The post-redirect handler in `main.tsx` reads
   * `repancy:portal-signin-pending` from sessionStorage to know which
   * portal route to navigate back to once token exchange completes.
   */
  signIn: () => Promise<AccountInfo | null>;
  signOut: () => Promise<void>;
}

const PortalMsalContext = createContext<PortalMsalContextValue | null>(null);

export function MsalInstanceProvider({
  lockedTenantId,
  children,
}: {
  lockedTenantId: string;
  children: ReactNode;
}) {
  const [ready, setReady] = useState(false);
  const [account, setAccount] = useState<AccountInfo | null>(null);

  // Acquire/release lifecycle.
  useEffect(() => {
    let cancelled = false;
    void acquireInstance(lockedTenantId)
      .then(() => {
        if (cancelled) {return;}
        setReady(true);
        setAccount(getActiveAccountForTenant(lockedTenantId));
      })
      .catch((err) => {
        // Surface initialization errors; the sign-in button will remain
        // disabled in this state.
        console.error('MsalInstanceProvider acquireInstance failed:', err);
        if (!cancelled) {
          setReady(false);
        }
      });
    return () => {
      cancelled = true;
      releaseInstance(lockedTenantId);
    };
  }, [lockedTenantId]);

  const signIn = useCallback(async () => {
    // F16.L: switched from loginPopup to loginRedirect. Modern Chrome
    // strips window.opener when the popup navigates through
    // login.microsoftonline.com (COOP) which blocks MSAL's
    // popup-bridge handoff. Redirect mode avoids the opener mechanic
    // entirely — the SPA navigates away, returns, and the boot
    // handler in main.tsx picks up the response via handleRedirectPromise.
    //
    // We still clear stale interaction.status locks first because a
    // prior popup-mode attempt can leave one stuck.
    try {
      for (const key of Object.keys(sessionStorage)) {
        if (key.startsWith('msal.') && key.endsWith('interaction.status')) {
          sessionStorage.removeItem(key);
        }
      }
    } catch {
      // sessionStorage unavailable — non-fatal; MSAL will surface its
      // own error if needed.
    }
    // Derive the slug from the current URL. The provider is mounted
    // inside `/c/$clientSlug/...` so the path's second segment is
    // always the slug.
    const path = window.location.pathname; // /c/<slug>/...
    const slug = path.split('/')[2] ?? '';
    const returnTo = `${window.location.pathname}${window.location.search}`;
    // This call navigates the SPA away. The promise resolves when the
    // navigation is queued; callers should not depend on a return value.
    await startPortalRedirectSignIn({
      lockedTenantId,
      slug,
      returnTo,
    });
    // Unreachable in the normal flow (loginRedirect navigated away).
    // We return null so the callback's contract is satisfied for tests.
    return null;
  }, [lockedTenantId]);

  const signOut = useCallback(async () => {
    await disconnectPortalAccount(lockedTenantId);
    setAccount(null);
  }, [lockedTenantId]);

  const value: PortalMsalContextValue = {
    lockedTenantId,
    ready,
    account,
    signIn,
    signOut,
  };

  return (
    <PortalMsalContext.Provider value={value}>
      {children}
    </PortalMsalContext.Provider>
  );
}

/** Throws if called outside an MsalInstanceProvider (i.e., outside a portal). */
export function usePortalMsal(): PortalMsalContextValue {
  const ctx = useContext(PortalMsalContext);
  if (ctx === null) {
    throw new Error(
      'usePortalMsal called outside of <MsalInstanceProvider>. ' +
        'Mount this component inside a `/c/${slug}` route subtree.',
    );
  }
  return ctx;
}

/** Same, but returns null when outside a provider. */
export function useOptionalPortalMsal(): PortalMsalContextValue | null {
  return useContext(PortalMsalContext);
}
